const { canonicalizePath, localizePath } = require('./localizedRoutes')

const SITE_NAME = 'ETC Company'
const DEFAULT_OG_IMAGE = '/logos/og-image.png'
const FALLBACK_SITE_URL = 'http://localhost:3000'

function normalizeSiteUrl(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '')
  return `https://${raw.replace(/\/+$/, '')}`
}

function getSiteUrl() {
  const envUrl =
    normalizeSiteUrl(process.env.NEXT_PUBLIC_SITE_URL) ||
    normalizeSiteUrl(process.env.APP_BASE_URL) ||
    normalizeSiteUrl(process.env.VERCEL_URL)

  if (envUrl) return envUrl
  if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
  return FALLBACK_SITE_URL
}

function stripQueryHash(value = '/') {
  const raw = String(value || '')
  const hashIndex = raw.indexOf('#')
  const queryIndex = raw.indexOf('?')
  const end =
    hashIndex === -1
      ? queryIndex === -1
        ? raw.length
        : queryIndex
      : queryIndex === -1
        ? hashIndex
        : Math.min(hashIndex, queryIndex)
  return raw.slice(0, end) || '/'
}

function absoluteUrl(path = '/', baseUrl = getSiteUrl()) {
  const safeBase = normalizeSiteUrl(baseUrl) || FALLBACK_SITE_URL
  const safePath = String(path || '/').startsWith('http')
    ? String(path || '/')
    : stripQueryHash(path)
  return new URL(safePath || '/', safeBase).toString()
}

function buildPageTitle(title) {
  const trimmed = String(title || '').trim()
  if (!trimmed) return SITE_NAME
  if (trimmed.toLowerCase().includes(SITE_NAME.toLowerCase())) return trimmed
  return `${trimmed} | ${SITE_NAME}`
}

function normalizeKeywords(keywords) {
  const values = Array.isArray(keywords)
    ? keywords
    : String(keywords || '')
        .split(',')
        .map((keyword) => keyword.trim())

  return [...new Set(values.map((keyword) => String(keyword || '').trim()).filter(Boolean))]
}

function resolveLocalizedKeywords(keywords, language = 'en') {
  if (!keywords || typeof keywords !== 'object' || Array.isArray(keywords)) {
    return normalizeKeywords(keywords)
  }

  const normalizedLanguage = String(language || 'en').toLowerCase() === 'fr' ? 'fr' : 'en'
  const fallbackLanguage = normalizedLanguage === 'fr' ? 'en' : 'fr'
  const candidates = [
    keywords[normalizedLanguage],
    keywords.default,
    keywords.all,
    keywords[fallbackLanguage],
  ]

  for (const candidate of candidates) {
    const normalized = normalizeKeywords(candidate)
    if (normalized.length) return normalized
  }

  return []
}

function buildPageKeywords(title, keywords = [], language = 'en') {
  const normalizedKeywords = resolveLocalizedKeywords(keywords, language)
  if (normalizedKeywords.length) return normalizedKeywords

  const trimmedTitle = String(title || '').trim()
  if (!trimmedTitle) return [SITE_NAME]

  const siteSuffix = `| ${SITE_NAME}`
  const normalizedTitle = trimmedTitle.toLowerCase().endsWith(siteSuffix.toLowerCase())
    ? trimmedTitle.slice(0, -siteSuffix.length).trim()
    : trimmedTitle

  return [normalizedTitle, SITE_NAME].filter(Boolean)
}

function getLocaleUrls(path = '/') {
  const normalizedPath = stripQueryHash(path)
  const routeFamily = canonicalizePath(normalizedPath) || '/'
  const enPath = routeFamily
  const frPath = localizePath(routeFamily, 'fr') || routeFamily

  return {
    currentPath: normalizedPath,
    routeFamily,
    enUrl: absoluteUrl(enPath),
    frUrl: absoluteUrl(frPath),
    currentUrl: absoluteUrl(normalizedPath),
  }
}

function getDefaultDescription(language = 'en') {
  if (String(language || '').toLowerCase() === 'fr') {
    return 'ETC Company propose des équipements de manutention, des pièces de rechange et un accompagnement commercial pour les équipes terrain.'
  }
  return 'ETC Company supplies warehouse equipment, spare parts, and sales support for operational teams.'
}

function safeJsonLd(data) {
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

module.exports = {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  absoluteUrl,
  buildPageTitle,
  buildPageKeywords,
  getDefaultDescription,
  getLocaleUrls,
  getSiteUrl,
  safeJsonLd,
  normalizeKeywords,
  resolveLocalizedKeywords,
  stripQueryHash,
}
